<div class="d-flex flex-column gap-3" data-testid="subscription-items-panel">
  <div class="d-flex flex-column gap-1">
    <h3 class="av-section-title m-0">{{ 'backoffice.subscriptions.overrides.title' | translate }}</h3>
    <p class="av-meta m-0">{{ 'backoffice.subscriptions.overrides.hint' | translate }}</p>
  </div>

  @if (facade.items().length === 0) {
    <av-empty-state
      icon="sell"
      [title]="'backoffice.subscriptions.overrides.no_items_title' | translate"
      [message]="'backoffice.subscriptions.overrides.no_items_message' | translate"
    ></av-empty-state>
  } @else {
    <div class="d-flex flex-wrap gap-2" data-testid="item-tabs">
      @for (item of facade.items(); track item.id) {
        <button
          type="button"
          class="av-btn"
          [class.av-btn-primary]="facade.selectedItemId() === item.id"
          (click)="selectItem(item.id)"
        >
          {{ componentLabelKey(item.component_type) | translate }}
        </button>
      }
    </div>

    @if (facade.selectedItem(); as item) {
      <div class="av-card p-3 d-flex flex-column gap-2" data-testid="item-detail">
        <div class="d-flex flex-wrap justify-content-between gap-3">
          <div class="d-flex flex-column gap-1">
            <span class="cell-strong">{{ productName(item.component_type) | translate }}</span>
            <span class="av-source">{{ item.stripe_subscription_item_id || unresolved }}</span>
          </div>

          <dl class="d-flex flex-wrap gap-4 m-0">
            <div class="d-flex flex-column">
              <dt class="av-meta">{{ 'backoffice.subscriptions.overrides.stripe_quantity' | translate }}</dt>
              <dd class="m-0 cell-strong" data-testid="item-quantity">{{ item.quantity }}</dd>
            </div>

            <div class="d-flex flex-column">
              <dt class="av-meta">{{ 'backoffice.subscriptions.overrides.licence_count' | translate }}</dt>
              <dd class="m-0 cell-strong" data-testid="item-licence-count">
                {{ item.licence_count }}
              </dd>
            </div>

            <div class="d-flex flex-column">
              <dt class="av-meta">{{ 'backoffice.subscriptions.overrides.unit_amount' | translate }}</dt>
              <dd class="m-0 cell-strong" data-testid="item-unit-amount">
                {{ item.unit_amount | number: '1.2-2' }} {{ item.currency || '' }}
              </dd>
            </div>
          </dl>
        </div>

        @if (activeOverride(); as override) {
          <div class="d-flex flex-wrap align-items-center gap-2" data-testid="item-active-override">
            <av-status-chip
              [label]="'backoffice.subscriptions.overrides.active_chip' | translate"
              tone="warning"
            ></av-status-chip>
            <span class="av-meta">
              {{
                'backoffice.subscriptions.overrides.active_summary'
                  | translate
                    : {
                        amount: override.override_unit_amount | number: '1.2-2',
                        currency: override.currency || '',
                        original: override.original_unit_amount | number: '1.2-2',
                      }
              }}
            </span>

            <button
              type="button"
              class="av-btn"
              [disabled]="!facade.canWrite()"
              (click)="openRevert(override)"
              data-testid="override-revert-active"
            >
              {{ 'backoffice.subscriptions.overrides.revert' | translate }}
            </button>
          </div>
        } @else {
          <div>
            <button
              type="button"
              class="av-btn av-btn-primary"
              [disabled]="!facade.canWrite()"
              (click)="openOverride()"
              data-testid="override-apply"
            >
              {{ 'backoffice.subscriptions.overrides.apply' | translate }}
            </button>
          </div>
        }
      </div>
    }

    @switch (facade.overridesState()) {
      @case ('error') {
        <av-empty-state
          icon="error_outline"
          [title]="'backoffice.subscriptions.overrides.history_error_title' | translate"
          [message]="'backoffice.subscriptions.overrides.history_error_message' | translate"
        ></av-empty-state>
      }
      @case ('ready') {
        @if (facade.overrides().length === 0) {
          <p class="av-meta m-0" data-testid="overrides-empty">{{ 'backoffice.subscriptions.overrides.empty' | translate }}</p>
        } @else {
          <div class="table-scroll">
            <table class="av-table w-100" data-testid="override-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.subscriptions.overrides.table.status' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.subscriptions.overrides.table.original' | translate }}</th>
                  <th scope="col" class="text-end">{{ 'backoffice.subscriptions.overrides.table.override' | translate }}</th>
                  <th scope="col">{{ 'backoffice.subscriptions.overrides.table.window' | translate }}</th>
                  <th scope="col">{{ 'backoffice.subscriptions.overrides.table.reason' | translate }}</th>
                  <th scope="col">{{ 'backoffice.subscriptions.overrides.table.applied_by' | translate }}</th>
                </tr>
              </thead>

              <tbody>
                @for (override of facade.overrides(); track override.id) {
                  <tr>
                    <td>
                      <av-status-chip
                        [label]="override.status || unresolved"
                        [tone]="override.statusTone"
                      ></av-status-chip>
                    </td>

                    <td class="text-end">
                      {{ override.original_unit_amount | number: '1.2-2' }}
                      {{ override.currency || '' }}
                    </td>

                    <td class="text-end cell-strong">
                      {{ override.override_unit_amount | number: '1.2-2' }}
                      {{ override.currency || '' }}
                    </td>

                    <td class="cell-muted">
                      {{ override.starts_at ? (override.starts_at | date: dateFormat) : unresolved }}
                      —
                      {{ override.ends_at ? (override.ends_at | date: dateFormat) : ('backoffice.subscriptions.overrides.open_ended' | translate) }}
                    </td>

                    <td class="cell-muted">{{ override.reason || unresolved }}</td>

                    <td class="cell-muted">{{ override.authorLabel }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
      @default {
        <p class="av-meta m-0">{{ 'backoffice.subscriptions.overrides.history_loading' | translate }}</p>
      }
    }
  }
</div>
